import type { Farmer, FarmerAttachmentEntry } from '../../types';
import { addFarmerAttachment } from '../../store';
import { showToast } from '../base/Toast';
import { DocumentUploadSlot } from './DocumentUploadSlot';
import type { ExtractionDocumentType } from '../../api/aiExtraction';
import type { FilePurpose } from '../../api/file';

const SLOTS: Array<{
  key: FarmerAttachmentEntry['type'];
  label: string;
  docType: ExtractionDocumentType;
  purpose: FilePurpose;
}> = [
  { key: 'CCCD_FRONT', label: 'CCCD/CMND (mặt trước)', docType: 'CCCD_FRONT', purpose: 'FARMER_IDENTITY_DOCUMENT' },
  { key: 'CCCD_BACK', label: 'CCCD/CMND (mặt sau)', docType: 'CCCD_BACK', purpose: 'FARMER_IDENTITY_DOCUMENT' },
  { key: 'LAND_TITLE', label: 'Giấy chứng nhận thửa đất', docType: 'LAND_TITLE', purpose: 'LAND_TITLE_DOCUMENT' },
  { key: 'OFFTAKE_CONTRACT', label: 'Hợp đồng bao tiêu', docType: 'OFFTAKE_CONTRACT', purpose: 'CONTRACT_DOCUMENT' },
];

/**
 * Post-creation document checklist — what CreateFarmerModal's own upload
 * step is missing once a profile already exists: a place to see which of
 * the expected documents were actually saved and which are still missing,
 * and to add/replace one without recreating the whole profile. Lives right
 * next to "Chỉnh sửa hồ sơ" per the explicit ask, not as a separate tab.
 */
export function FarmerAttachmentsSection({ farmer, htxId, editable }: { farmer: Farmer; htxId?: string; editable: boolean }) {
  const attachmentsByType = new Map((farmer.attachments ?? []).map((a) => [a.type, a]));
  const missingCount = SLOTS.filter((s) => !attachmentsByType.has(s.key)).length;

  async function handleUploaded(type: FarmerAttachmentEntry['type'], fileId: string) {
    try {
      await addFarmerAttachment(farmer.id, { fileId, type }, htxId);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Lưu tài liệu thất bại', 'error');
    }
  }

  return (
    <div className="mt-4 pt-4 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-slate-900">Tài liệu đính kèm</p>
        {missingCount > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Còn thiếu {missingCount}</span>
        )}
      </div>
      {!editable && (
        <p className="mb-2 text-[11px] text-slate-400">Hồ sơ đã đồng bộ với tài khoản nông hộ — chỉ nông hộ mới thêm/thay tài liệu được.</p>
      )}
      <div className="space-y-2">
        {SLOTS.map((slot) => {
          const existing = attachmentsByType.get(slot.key);
          if (!editable) {
            return (
              <div key={slot.key} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-700">{slot.label}</p>
                <span className={`text-[10px] ${existing ? 'text-agrida-600' : 'text-slate-400'}`}>{existing ? '✓ Đã có' : 'Chưa có'}</span>
              </div>
            );
          }
          return (
            <DocumentUploadSlot
              key={slot.key}
              label={slot.label}
              docType={slot.docType}
              purpose={slot.purpose}
              existingFileId={existing?.fileId}
              onUploaded={(fileId) => handleUploaded(slot.key, fileId)}
            />
          );
        })}
      </div>
    </div>
  );
}
